'use client'

/**
 * Activity tracker utility for monitoring user interaction
 * Used to determine when to refresh the session
 */

const ACTIVITY_STORAGE_KEY = 'portal:lastActivity';

export const activityTracker = {
  /**
   * Initialize activity tracking
   * Sets up event listeners for user interactions
   */
  init: () => {
    if (typeof window === 'undefined') return;

    // Update timestamp on initial load
    activityTracker.updateLastActivity();

    // Track user interactions
    const events = ['mousedown', 'keydown', 'mousemove', 'touchstart', 'scroll', 'click'];
    
    // Throttle the update to prevent excessive writes
    let timeout: NodeJS.Timeout | null = null;
    
    const handleActivity = () => {
      if (timeout) return;
      
      timeout = setTimeout(() => {
        activityTracker.updateLastActivity();
        timeout = null;
      }, 1000); // Throttle to once per second
    };

    // Add event listeners
    events.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Return cleanup function
    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      if (timeout) clearTimeout(timeout);
    };
  },

  /**
   * Update the timestamp of the last user activity
   */
  updateLastActivity: () => {
    if (typeof window === 'undefined') return;
    
    const now = Date.now();
    localStorage.setItem(ACTIVITY_STORAGE_KEY, now.toString());
    return now;
  },

  /**
   * Get the timestamp of the last user activity
   * @returns Timestamp in milliseconds or current time if not available
   */
  getLastActivity: (): number => {
    if (typeof window === 'undefined') return Date.now();
    
    const stored = localStorage.getItem(ACTIVITY_STORAGE_KEY);
    return stored ? parseInt(stored, 10) : Date.now();
  },

  /**
   * Check if there has been user activity within the specified time window
   * @param withinMs Time window in milliseconds
   * @returns True if there has been activity within the specified window
   */
  hasActivityWithin: (withinMs: number): boolean => {
    const lastActivity = activityTracker.getLastActivity();
    const now = Date.now();
    return (now - lastActivity) <= withinMs;
  },

  /**
   * Get time elapsed since last activity in milliseconds
   * @returns Time elapsed in milliseconds
   */
  getTimeSinceLastActivity: (): number => {
    const lastActivity = activityTracker.getLastActivity();
    return Date.now() - lastActivity;
  }
};
